<script setup lang="ts">
import { computed, markRaw, ref } from 'vue'

import IconSettingLayout from '@/components/atoms/icons/IconSettingLayout.vue'
import IconSettingText from '@/components/atoms/icons/IconSettingText.vue'
import SettingBorderRadius from '@/components/atoms/SettingBorderRadius.vue'
import SettingLink from '@/components/atoms/SettingLink.vue'
import SettingMargin from '@/components/atoms/SettingMargin.vue'
import SettingPadding from '@/components/atoms/SettingPadding.vue'
import SettingTypeButton from '@/components/atoms/SettingTypeButton.vue'
import SettingWhatsapp from '@/components/atoms/SettingWhatsapp.vue'
import SettingWidth from '@/components/atoms/SettingWidth.vue'
import EditorSidebarLayout from '@/components/layouts/EditorSidebarLayout.vue'
import SettingsLayout from '@/components/layouts/SettingsLayout.vue'
import BetaSettingColorPicker from '@/components/beta-editor/settings/controls/BetaSettingColorPicker.vue'
import BetaSettingFontSize from '@/components/beta-editor/settings/controls/BetaSettingFontSize.vue'
import BetaSettingFontWeight from '@/components/beta-editor/settings/controls/BetaSettingFontWeight.vue'
import BetaSettingTextAlign from '@/components/beta-editor/settings/controls/BetaSettingTextAlign.vue'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useLayoutStore } from '@/stores/layout'

const betaStore = useEditorBetaStore()
const layoutStore = useLayoutStore()

const elementForEdit = computed(() => betaStore.elementForEdit)
const buttonFontSizeDefault = computed(() => elementForEdit.value?.extra?.style?.fontSize ?? '16px')
const buttonFontWeightDefault = computed(() => (
    layoutStore.css?.button_font_weight
    ?? elementForEdit.value?.extra?.style?.fontWeight
    ?? '700'
))
const buttonTextAlignDefault = computed(() => elementForEdit.value?.extra?.style?.textAlign ?? 'left')
const settings = ref([
    { id: 1, name: 'button', icon: markRaw(IconSettingText) },
    { id: 2, name: 'layout', icon: markRaw(IconSettingLayout) },
])

const ensureFrameContent = () => {
    if (!elementForEdit.value) return

    elementForEdit.value.frameContent = {
        settings: {
            type: 'url',
            target: '_self',
            ...(elementForEdit.value.frameContent?.settings ?? {}),
        },
        dataPhone: {
            idd: '',
            phone: '',
            ...(elementForEdit.value.frameContent?.dataPhone ?? {}),
        },
        ...(elementForEdit.value.frameContent ?? {}),
    }
}

const onChangeWidth = (event: any) => {
    if (!elementForEdit.value) return

    if (event.target === 'desktop') elementForEdit.value.grid = event.value
    if (event.target === 'mobile') elementForEdit.value.gridMobile = event.value

    betaStore.updateElementForEdit()
}

const onChangeStyle = (event: any) => {
    if (!elementForEdit.value) return

    const styleTargets: Record<string, string> = {
        'font-size': 'fontSize',
        'font-weight': 'fontWeight',
        'text-align': 'textAlign',
        'border-radius': 'borderRadius',
    }

    if (styleTargets[event.target]) {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            style: {
                ...elementForEdit.value.extra?.style,
                [styleTargets[event.target]]: event.value,
            },
        }
    }

    const lockTargets = [
        'style_font_size_lock',
        'style_font_weight_lock',
        'style_text_align_lock',
        'style_border_radius_lock',
    ]

    if (lockTargets.includes(event.target)) {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [event.target]: event.value,
        }
    }

    betaStore.updateElementForEdit()
}

const onChangeColor = (value: string) => {
    if (!elementForEdit.value) return

    elementForEdit.value.extra = {
        ...elementForEdit.value.extra,
        style: {
            ...elementForEdit.value.extra?.style,
            color: value,
        },
    }

    betaStore.updateElementForEdit()
}

const onChangeBackgroundColor = (value: string) => {
    if (!elementForEdit.value) return

    elementForEdit.value.extra = {
        ...elementForEdit.value.extra,
        style: {
            ...elementForEdit.value.extra?.style,
            backgroundColor: value,
        },
    }

    betaStore.updateElementForEdit()
}

const onChangeLink = (event: any) => {
    if (!elementForEdit.value) return
    ensureFrameContent()

    if (event.target === 'link') {
        elementForEdit.value.frameContent.ref = event.value
    }

    if (event.target === 'open-in') {
        elementForEdit.value.frameContent.settings = {
            ...elementForEdit.value.frameContent.settings,
            target: event.value,
        }
    }

    betaStore.updateElementForEdit()
}

const onChangeTypeButton = (event: any) => {
    if (!elementForEdit.value) return
    ensureFrameContent()

    elementForEdit.value.frameContent.settings = {
        ...elementForEdit.value.frameContent.settings,
        type: event.value,
    }

    betaStore.updateElementForEdit()
}

const onChangeWhatsapp = (event: any) => {
    if (!elementForEdit.value) return
    ensureFrameContent()

    elementForEdit.value.frameContent.dataPhone = {
        ...elementForEdit.value.frameContent.dataPhone,
        [event.target]: event.value,
    }

    betaStore.updateElementForEdit()
}

const onChangeSpacing = (event: any, target: 'margin' | 'padding') => {
    if (!elementForEdit.value) return

    if (event.config === 'desktop') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [target]: {
                ...elementForEdit.value.extra?.[target],
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'mobile') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [`${target}_mobile`]: {
                ...elementForEdit.value.extra?.[`${target}_mobile`],
                [event.target]: event.value,
            },
        }
    }

    if (event.config === 'lock') {
        elementForEdit.value.extra = {
            ...elementForEdit.value.extra,
            [`${target}_mobile_lock`]: event.value,
        }
    }

    betaStore.updateElementForEdit()
}
</script>

<template>
    <editor-sidebar-layout v-if="elementForEdit" :settings="settings">
        <template #button>
            <settings-layout name="Configurações do Botão" :default-open="true">
                <beta-setting-color-picker
                    label="Cor do Texto"
                    :value="elementForEdit.extra?.style?.color"
                    :default-value="layoutStore.getButtonColor(elementForEdit.sectionIndex) ?? ''"
                    clear-on-default
                    @change="onChangeColor"
                />

                <beta-setting-font-size
                    label="Tamanho da Fonte"
                    :value="elementForEdit.extra?.style?.fontSize"
                    :value-default="buttonFontSizeDefault"
                    :lock="elementForEdit.extra?.style_font_size_lock"
                    :show-switch="true"
                    @change="onChangeStyle"
                />

                <beta-setting-font-weight
                    label="Espessura da Fonte"
                    :value="elementForEdit.extra?.style?.fontWeight"
                    :value-default="buttonFontWeightDefault"
                    :lock="elementForEdit.extra?.style_font_weight_lock"
                    :show-switch="true"
                    @change="onChangeStyle"
                />

                <beta-setting-text-align
                    label="Alinhamento"
                    :value="elementForEdit.extra?.style?.textAlign"
                    :value-default="buttonTextAlignDefault"
                    :lock="elementForEdit.extra?.style_text_align_lock"
                    :show-switch="true"
                    @change="onChangeStyle"
                />

                <beta-setting-color-picker
                    label="Cor do Botão"
                    :value="elementForEdit.extra?.style?.backgroundColor"
                    :default-value="layoutStore.getButtonBackgroundColor(elementForEdit.sectionIndex) ?? ''"
                    clear-on-default
                    @change="onChangeBackgroundColor"
                />

                <setting-border-radius
                    label="Borda Arredondada"
                    :value="elementForEdit.extra?.style?.borderRadius"
                    :lock="elementForEdit.extra?.style_border_radius_lock"
                    @change="onChangeStyle"
                />

                <setting-type-button
                    label="Tipo de Botão"
                    :value="elementForEdit.frameContent?.settings?.type ?? 'url'"
                    @change="onChangeTypeButton"
                />

                <setting-link
                    v-if="(elementForEdit.frameContent?.settings?.type ?? 'url') === 'url'"
                    label="Link"
                    type="string"
                    :value="elementForEdit.frameContent?.ref"
                    :value-open-in="elementForEdit.frameContent?.settings?.target"
                    @change="onChangeLink"
                />

                <setting-whatsapp
                    v-if="elementForEdit.frameContent?.settings?.type === 'whatsapp'"
                    label="Telefone"
                    :idd="elementForEdit.frameContent?.dataPhone?.idd"
                    :phone="elementForEdit.frameContent?.dataPhone?.phone"
                    @change="onChangeWhatsapp"
                />
            </settings-layout>
        </template>

        <template #layout>
            <settings-layout name="Configurações do Conteúdo" :default-open="true">
                <setting-width
                    label="Largura"
                    :value="elementForEdit.grid"
                    :value-mobile="elementForEdit.gridMobile"
                    :values-permission-desktop="[4, 6, 12]"
                    :values-permission-mobile="[4, 6, 12]"
                    @change="onChangeWidth"
                />
            </settings-layout>

            <settings-layout name="Avançado">
                <setting-margin
                    label="Margem Externa"
                    :value="elementForEdit.extra?.margin"
                    :value-mobile="elementForEdit.extra?.margin_mobile"
                    :lock="elementForEdit.extra?.margin_mobile_lock"
                    @change="(event: any) => onChangeSpacing(event, 'margin')"
                />

                <setting-padding
                    label="Margem Interna"
                    :value="elementForEdit.extra?.padding"
                    :value-mobile="elementForEdit.extra?.padding_mobile"
                    :lock="elementForEdit.extra?.padding_mobile_lock"
                    @change="(event: any) => onChangeSpacing(event, 'padding')"
                />
            </settings-layout>
        </template>
    </editor-sidebar-layout>
</template>
